import { ActivatedRoute, Router } from '@angular/router';
import { FiltersConfig } from './filter.models';
import { MenuItem } from 'primeng/api';
import { PColumn } from '../components/quick-table/quick-table';
import { Directive, output, input, Inject, inject } from '@angular/core';

export interface OnActionEvent {
  action?: string;
  item?: any;
  menuItem?: MenuItem;
}

/**
 * Abstract base class for implementing pagination functionality.
 * Provides common pagination logic, state management, and navigation handling.
 * Child classes must implement the loadData method and define columns.
 */
@Directive()
export abstract class PaginationBase {
  /**
   * Available actions for items.
   * Must be implemented by child classes.
   */
  readonly actions = input<MenuItem[]>();
  /**
   * Event emitted when an action is performed on an item.
   * Emits an object containing the item and the action that was triggered.
   */
  readonly onAction = output<OnActionEvent>();

  /** Current first record index for the paginator */
  public paginatorFirst: number = 0;
  /** Number of rows to display per page */
  public paginatorRows: number = 10;
  /** Total number of records available */
  public totalRecords: number = 0;
  /** Current view mode, either cards or table */
  public viewMode: 'cards' | 'table' = 'table';
  /** Loading state indicator */
  public isLoading: boolean = false;
  /** Available button actions for items */
  public buttonActions: MenuItem[] = [];

  /**
   * Columns configuration for the table view.
   * Must be implemented by child classes.
   */
  protected abstract columns: PColumn[];

  /**
   * Configuration for filtering, sorting, and pagination.
   * Includes current page, rows per page, sorting criteria, and applied filters.
   */
  public filterConfig: FiltersConfig = { page: 0, rowsPerPage: this.paginatorRows, sort: { _id: -1 }, filters: {} };

  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  /**
   * Creates an instance of PaginationBase.
   * @param route - The ActivatedRoute for accessing route parameters
   * @param router - The Router for navigation
   */
  constructor() {
    this.initializePagination();
  }

  /**
   * Initializes pagination based on URL query parameters.
   * Sets the current page from the 'page' query parameter if available.
   * @private
   */
  private initializePagination(): void {
    const page = this.route.snapshot.queryParamMap.get('page');
    if (page) {
      this.filterConfig.page = +page - 1;
      this.paginatorFirst = this.filterConfig.page * this.paginatorRows;
    }
  }

  /**
   * Handles pagination events from the paginator component.
   * Updates the filter configuration, loads new data, and updates the URL.
   * @param pageEvent - The page event containing page number and rows per page
   */
  public async onPageChange(pageEvent: any) {
    this.paginatorRows = pageEvent.rows;
    this.filterConfig = {
      ...this.filterConfig,
      page: pageEvent.page,
      rowsPerPage: this.paginatorRows,
    };

    await this.loadData();
    this.updatePageParam(pageEvent.page + 1);
  }

  /**
   * Updates the 'page' query parameter in the URL.
   * @param newPage - The new page number to set in the URL
   * @private
   */
  private updatePageParam(newPage: number) {
    const queryParams = { page: newPage };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  // Abstract method that needs to be implemented by child classes
  /**
   * Loads data for the current page configuration.
   * Must be implemented by child classes to fetch data from the appropriate source.
   * @returns A promise that resolves when data loading is complete
   */
  protected abstract loadData(): Promise<void>;

  /**
   * Handle actions triggered from the table or card view
   * @param param0 Object containing the item and action that was triggered
   */
  public doAction(actionEvent: OnActionEvent) {
    this.onAction.emit(actionEvent);
  }
}
